import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    number: "1",
    title: "Copy the Rightmove URL",
    description:
      "Find the listing on Rightmove, copy the URL from your browser. That's the only input QuiteRight needs.",
  },
  {
    number: "2",
    title: "QuiteRight imports everything",
    description:
      "Photos, price, address, key features, number of bedrooms, property type. All pulled automatically from the Rightmove listing page.",
  },
  {
    number: "3",
    title: "Pick a template and publish",
    description:
      "Choose from Prestige, Modern, Classic, or Bold. QuiteRight generates the script, voiceover, and music. Your branded video is ready in 60 seconds.",
  },
]

const FEATURES = [
  {
    title: "Full listing data import",
    description:
      "Property photos, price, address, bedrooms, bathrooms, and key features are all extracted automatically. No manual data entry.",
  },
  {
    title: "Works with every property type",
    description:
      "Detached houses, flats, bungalows, new builds, commercial, and land. If it's on Rightmove, QuiteRight can turn it into a video.",
  },
  {
    title: "AI voiceover from listing details",
    description:
      "QuiteRight generates a property-specific script from the listing description and delivers it as a professional AI voiceover. Multiple voices available.",
  },
  {
    title: "All social formats included",
    description:
      "Every video comes ready for Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn. Portrait, landscape, and square formats.",
  },
  {
    title: "Your branding on every video",
    description:
      "Upload your logo once. Every video includes your agency name, colours, and contact details. Consistent branding across your entire Rightmove portfolio.",
  },
  {
    title: "Bulk production at scale",
    description:
      "Got 50 Rightmove listings this month? Create 50 videos in under an hour. Each one takes about 60 seconds to generate.",
  },
]

const STATS = [
  { stat: "60s", label: "from Rightmove URL to finished video" },
  { stat: "100%", label: "of listing photos imported automatically" },
  { stat: "50", label: "videos per month on the Managed plan" },
  { stat: "6+", label: "social media formats from one video" },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Does QuiteRight work with any Rightmove listing?",
    a: "Yes. QuiteRight works with residential sales, lettings, new homes, and commercial listings on Rightmove. Simply paste the listing URL and QuiteRight extracts all the data it needs automatically.",
  },
  {
    q: "Do I need to download photos from Rightmove first?",
    a: "No. QuiteRight pulls the photos directly from the Rightmove listing page. You don't need to download, upload, or manage any image files. Just paste the URL.",
  },
  {
    q: "Can I use Rightmove photos in social media videos?",
    a: "Yes. The photos on your Rightmove listing were commissioned by you (or your photographer) for marketing the property. Using them in a video ad to promote that same listing is standard marketing practice.",
  },
  {
    q: "How quickly is the video ready after pasting a Rightmove URL?",
    a: "About 60 seconds. QuiteRight imports the listing data, generates a script and voiceover, selects music, applies your branding, and renders the video. You can be posting to Instagram before your kettle boils.",
  },
  {
    q: "What if the Rightmove listing is updated after I create a video?",
    a: "You can create a new video at any time by pasting the updated URL. Price changes, new photos, or updated descriptions are all picked up automatically. There is no extra charge for regenerating videos.",
  },
  {
    q: "Does QuiteRight also work with Zoopla and OnTheMarket?",
    a: "Yes. QuiteRight supports all three major UK portals: Rightmove, Zoopla, and OnTheMarket. The process is identical: paste the listing URL and your video is ready in 60 seconds.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function IntegrationRightmove({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Create Video Ads from Rightmove Listings",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Integrations", path: "/integrations" },
            { name: "Rightmove" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Integration
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Turn Rightmove listings into video ads
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Paste any Rightmove URL. QuiteRight imports the photos, price,
                address, and features, then creates a branded video ad in
                60 seconds. No filming, no editing, no design skills.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">From Rightmove to video</p>
                <p className="font-display text-xl font-bold text-white">60 seconds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              How it works
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Three steps. One URL. Done
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((step) => (
              <ScrollReveal key={step.number}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <span className="flex size-10 items-center justify-center rounded-full bg-red font-display text-lg font-bold text-white">
                    {step.number}
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-charcoal">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate">
                    {step.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              What you get
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Everything your Rightmove listing needs
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {feature.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {STATS.map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("integrations/rightmove")} tone="white" />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Your Rightmove listings deserve video
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Paste a URL, pick a template, and your branded video is ready to
              share. Try QuiteRight free for 14 days.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
